export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  tags: string[];
  content?: string;
}

export interface BlogPostData extends BlogPost {
  content: string;
}

// Simple markdown-like text processing for basic formatting
const processMarkdown = (text: string): string => {
  // Store code blocks with placeholders to protect from further processing
  const codeBlocks: string[] = [];

  let processed = text
    // Process code blocks FIRST, storing them with placeholders
    .replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
      const placeholder = `___CODE_BLOCK_${codeBlocks.length}___`;
      if (lang === 'mermaid') {
        const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
        codeBlocks.push(`<pre class="mermaid-diagram" data-diagram-id="${id}">${code.trim()}</pre>`);
      } else {
        codeBlocks.push(`<pre><code>${code}</code></pre>`);
      }
      return placeholder;
    })
    // Then process headers
    .replace(/^# (.*$)/gm, "<h1>$1</h1>")
    .replace(/^## (.*$)/gm, "<h2>$1</h2>")
    .replace(/^### (.*$)/gm, "<h3>$1</h3>")
    // Then inline formatting
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/`(.*?)`/g, "<code>$1</code>")
    // Finally newlines and paragraphs
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br>")
    .replace(/^(.*)$/gm, "<p>$1</p>")
    .replace(/<p><h/g, "<h")
    .replace(/<\/h([1-6])><\/p>/g, "</h$1>")
    .replace(/<p><\/p>/g, "");

  // Restore code blocks
  codeBlocks.forEach((block, index) => {
    processed = processed.replace(`___CODE_BLOCK_${index}___`, block);
  });

  return processed;
};

// Parse frontmatter from markdown content
const parseFrontmatter = (content: string): { data: any; content: string } => {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = content.match(frontmatterRegex);

  if (!match) {
    return { data: {}, content };
  }

  const frontmatterText = match[1];
  const markdownContent = match[2];

  // Simple YAML-like parsing for frontmatter
  const data: any = {};
  frontmatterText.split("\n").forEach((line) => {
    const colonIndex = line.indexOf(":");
    if (colonIndex > 0) {
      const key = line.substring(0, colonIndex).trim();
      let rawValue = line.substring(colonIndex + 1).trim();

      // Remove quotes
      if (rawValue.startsWith('"') && rawValue.endsWith('"')) {
        rawValue = rawValue.slice(1, -1);
      }

      // Handle arrays (simple bracket notation)
      if (rawValue.startsWith("[") && rawValue.endsWith("]")) {
        const arrayValue = rawValue
          .slice(1, -1)
          .split(",")
          .map((item) => item.trim().replace(/['"]/g, ""));
        data[key] = arrayValue;
      } else {
        data[key] = rawValue;
      }
    }
  });

  return { data, content: markdownContent };
};

// Get list of available markdown files from index
export const getMarkdownFiles = async (): Promise<string[]> => {
  try {
    const response = await fetch("/data/blog-index.json");
    if (!response.ok) {
      throw new Error("Failed to fetch blog index");
    }
    const files = await response.json();
    return Array.isArray(files) ? files : [];
  } catch (error) {
    console.error("Error getting markdown files:", error);
    return [];
  }
};

// Parse markdown file and extract frontmatter
export const parseMarkdownFile = async (
  filename: string,
): Promise<BlogPostData | null> => {
  try {
    const response = await fetch(`/data/posts/${filename}.md`);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${filename}.md`);
    }

    const markdownContent = await response.text();
    const { data: frontmatter, content } = parseFrontmatter(markdownContent);

    // Convert markdown to HTML
    const htmlContent = processMarkdown(content);

    // Validate required frontmatter fields
    if (!frontmatter.title || !frontmatter.excerpt) {
      console.warn(`Blog post ${filename} is missing required frontmatter (title or excerpt)`);
      return null;
    }

    return {
      slug: filename,
      title: frontmatter.title,
      excerpt: frontmatter.excerpt,
      date: frontmatter.date || new Date().toISOString().split("T")[0],
      tags: Array.isArray(frontmatter.tags) ? frontmatter.tags : [],
      content: htmlContent,
    };
  } catch (error) {
    console.error(`Error parsing markdown file ${filename}:`, error);
    return null;
  }
};

// Get all blog posts
export const getAllBlogPosts = async (): Promise<BlogPost[]> => {
  try {
    const files = await getMarkdownFiles();
    const posts: BlogPost[] = [];

    for (const file of files) {
      const post = await parseMarkdownFile(file);
      if (post) {
        // Remove content for list view to reduce payload
        const { content, ...postSummary } = post;
        posts.push(postSummary);
      }
    }

    // Sort by date, newest first
    return posts.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  } catch (error) {
    console.error("Error getting all blog posts:", error);
    return [];
  }
};

// Get a single blog post by slug
export const getBlogPost = async (
  slug: string,
): Promise<BlogPostData | null> => {
  try {
    return await parseMarkdownFile(slug);
  } catch (error) {
    console.error(`Error getting blog post ${slug}:`, error);
    return null;
  }
};
